import { ROUTE_SEARCH_PARAM } from 'src/constants/route';
import { ClientLog } from 'src/constants/clientLog';
import Logger from 'src/utils/logger';

// Matches ref parameters: ref=value, ref_=value, ref%3Dvalue (URL encoded)
const REF_PATTERN = /^ref(_)?(%3D|=)(.+)$/;

interface RefSegmentMatch {
  segmentIndex: number;
  refValue: string;
}

export const findRefSegment = (pathSegments: string[]): RefSegmentMatch | null => {
  let refSegmentIndex = -1;
  let validatedRefValue = '';

  const refSegment = pathSegments.find((segment, index) => {
    const decodedSegment = decodeURIComponent(segment);
    const segmentMatch = decodedSegment.match(REF_PATTERN);

    if (segmentMatch && segmentMatch.length > 3) {
      const refValue = segmentMatch[3];
      if (refValue && refValue.trim() !== '') {
        refSegmentIndex = index;
        validatedRefValue = refValue;
        return true;
      }
    }

    return false;
  });

  if (!refSegment || refSegmentIndex === -1 || !validatedRefValue) {
    return null;
  }

  return {
    segmentIndex: refSegmentIndex,
    refValue: validatedRefValue,
  };
};

export const constructNewPath = (pathSegments: string[], refSegmentIndex: number): string => {
  const newPathSegments = [...pathSegments];
  newPathSegments.splice(refSegmentIndex, 1);
  return `/${newPathSegments.join('/')}`;
};

export const createSearchParamsWithRef = (currentSearch: string, refValue: string): URLSearchParams => {
  const searchParams = new URLSearchParams(currentSearch);
  searchParams.set(ROUTE_SEARCH_PARAM.REF, refValue);
  return searchParams;
};

export const constructNewURL = (newPath: string, searchParams: URLSearchParams, hash: string): string => {
  return `${newPath}?${searchParams.toString()}${hash}`;
};

/**
 * Extracts ref tag from search parameters with priority given to ref_ over ref.
 * Returns undefined if neither parameter exists.
 *
 * @param searchParams - URLSearchParams object to extract ref tag from
 * @returns The ref tag value, or undefined if not found
 *
 * @example
 * // ref_ takes priority when both are present
 * extractRefTag(new URLSearchParams('?ref_=priority&ref=legacy')) // returns 'priority'
 *
 * @example
 * // Returns ref when only ref is present
 * extractRefTag(new URLSearchParams('?ref=legacy')) // returns 'legacy'
 *
 * @example
 * // Returns undefined when neither exists
 */
export const extractRefTag = (searchParams: URLSearchParams): string | undefined => {
  // Priority 1: ref_ parameter
  const refUnderscore = searchParams.get(ROUTE_SEARCH_PARAM.REF);
  if (refUnderscore && refUnderscore.trim() !== '') {
    return refUnderscore.trim();
  }

  // Priority 2: ref parameter (legacy)
  const ref = searchParams.get(ROUTE_SEARCH_PARAM.REF_LEGACY);
  if (ref && ref.trim() !== '') {
    return ref.trim();
  }

  // Priority 3: refTag parameter (legacy)
  const refTag = searchParams.get(ROUTE_SEARCH_PARAM.REF_TAG_LEGACY);
  if (refTag && refTag.trim() !== '') {
    return refTag.trim();
  }

  // No ref tag found
  return undefined;
};

/**
 * Extracts referral parameter from search parameters.
 * Returns undefined if the parameter doesn't exist.
 *
 * @param searchParams - URLSearchParams object to extract referral from
 * @returns The referral value, or undefined if not found
 *
 * @example
 * // Returns referral when present
 * extractReferral(new URLSearchParams('?referral=email')) // returns 'email'
 *
 * @example
 * // Returns undefined when not present
 * extractReferral(new URLSearchParams('?ref=test')) // returns undefined
 */
export const extractReferral = (searchParams: URLSearchParams): string | undefined => {
  const referral = searchParams.get(ROUTE_SEARCH_PARAM.REFERRAL);
  if (referral && referral.trim() !== '') {
    return referral.trim();
  }

  return undefined;
};

/**
 * Converts ref parameters from path segments to query parameters
 * Sanitizes malformed query parameters in URLs
 * Fixes patterns like &?param=value to &param=value
 */
export const sanitizeQueryParameters = (url: string): string => {
  return url.replace(/&\?/g, '&');
};

/**
 * Converts ref parameters from path segments to query parameters and sanitizes malformed query parameters
 * Handles URLs like /learn/ref=abc123 -> /learn?ref=abc123
 * Also fixes malformed query parameters like &?param=value -> &param=value
 */
export const handleRefParameterConversion = (): void => {
  try {
    const originalUrl = window.location.href;

    // Step 1: Sanitize malformed query parameters
    const sanitizedUrl = sanitizeQueryParameters(originalUrl);
    const wasSanitized = sanitizedUrl !== originalUrl;

    // Step 2: Update the URL if sanitization was needed
    if (wasSanitized) {
      window.history.replaceState(null, '', sanitizedUrl);
    }

    // Step 3: Parse refTag/referral from sanitized URL once
    const currentURL = new URL(sanitizedUrl, window.location.origin);
    const searchParams = new URLSearchParams(currentURL.search);
    const refTag = extractRefTag(searchParams);
    const referral = extractReferral(searchParams);

    // Log if sanitization was needed (always, regardless of ref/referral presence)
    if (wasSanitized) {
      Logger.warn(ClientLog.PayloadProcessingError, {
        message: 'URL query parameters were sanitized due to malformed syntax',
        originalUrl,
        sanitizedUrl,
        refTag,
        referral,
        operation: 'QueryParameterSanitization',
        context: 'URL query parameter sanitization',
      });
    }

    // Log ref/referral params received
    if (refTag || referral) {
      Logger.info(ClientLog.RefParameterReceived, {
        message: 'Ref or referral parameter detected in URL',
        url: originalUrl,
        refTag,
        referral,
        source: 'initial_load',
        operation: 'InitialURLCheck',
      });
    }

    // Step 4: Continue with ref parameter conversion from path to query params
    const pathSegments = currentURL.pathname.split('/').filter(Boolean);
    const refMatch = findRefSegment(pathSegments);

    if (!refMatch) {
      return;
    }

    const newPath = constructNewPath(pathSegments, refMatch.segmentIndex);
    const refSearchParams = createSearchParamsWithRef(currentURL.search, refMatch.refValue);
    const newURL = constructNewURL(newPath, refSearchParams, currentURL.hash);

    window.history.replaceState(null, '', newURL);
  } catch (error) {
    Logger.error(ClientLog.PayloadProcessingError, error instanceof Error ? error : new Error(String(error)), {
      context: 'URL processing and navigation',
      url: window.location.href,
      operation: 'RefParameterConversion',
    });
  }
};
